import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { apiServices } from '../../api/services';
import { ExerciseDefinition, WorkoutHistory } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/provider';
import { seoConfig } from '../../utils/seoConfig';
import StructuredData from '../seo/StructuredData';
import PageShell from '../layout/PageShell';
import ExerciseInfoModal from './ExerciseInfoModal';
import styles from './ProgressPage.module.css';

interface TopExercise {
  name: string;
  count: number;
  lastUsed: Date;
}

const ANON_WORKOUT_HISTORY_KEY = 'gym_planner_anonymous_workout_history';

const loadAnonymousWorkoutHistory = (): WorkoutHistory[] => {
  try {
    const raw = localStorage.getItem(ANON_WORKOUT_HISTORY_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as Array<WorkoutHistory & { date: string }>;
    return parsed.map((workout) => ({
      ...workout,
      date: new Date(workout.date),
    }));
  } catch (error) {
    console.warn('Failed to load anonymous workout history for progress page:', error);
    return [];
  }
};

 const ProgressPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Log component mount once
  useEffect(() => {
    console.log('📊 ProgressPage mounted');
  }, []);
  const [timeRange, setTimeRange] = useState<number | null>(90); // days (3 months)
  const [topExercises, setTopExercises] = useState<TopExercise[]>([]);
  // Multi-series line data: each entry has date + one key per muscle group
  const [lineData, setLineData] = useState<Array<Record<string, any>>>([]);
  const [pieData, setPieData] = useState<Array<{ name: string; value: number; color: string }>>([]);
  const [selectedExercise, setSelectedExercise] = useState<ExerciseDefinition | null>(null);
  const [exerciseMap, setExerciseMap] = useState<Map<string, ExerciseDefinition>>(new Map());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Always read the latest history from the mock store
    const loadHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const history = user
          ? ((await apiServices.workoutHistory.getWorkoutHistory(user.id, 1, 1000)) || { data: [] }).data
          : loadAnonymousWorkoutHistory();

        if (!history || history.length === 0) {
          setTopExercises([]);
          setLineData([]);
          setPieData([]);
          return;
        }

    // Sort ascending by date
    const sorted = [...history].sort((a, b) => {
      const dateA = typeof a.date === 'string' ? new Date(a.date) : a.date;
      const dateB = typeof b.date === 'string' ? new Date(b.date) : b.date;
      return dateA.getTime() - dateB.getTime();
    });

    // Filter by time range
    let filtered = sorted;
    if (timeRange !== null) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - timeRange);
      filtered = sorted.filter(w => {
        const workoutDate = typeof w.date === 'string' ? new Date(w.date) : w.date;
        return workoutDate >= cutoffDate;
      });
    }

    // ✅ EFFICIENT: Fetch all exercises once and create lookup map
    console.log('📦 Loading all exercises for progress analytics');
    const allExercises = await apiServices.exercises.getExercises();
    const newExerciseMap = new Map<string, ExerciseDefinition>();
    allExercises.forEach(ex => newExerciseMap.set(ex.name, ex));
    setExerciseMap(newExerciseMap);

    // Compute top 7 exercises by occurrence within the selected time range
    const counts = new Map<string, { count: number; last: Date }>();
    filtered.forEach(w => {
      // Count an exercise once per workout occurrence
      w.exercises.forEach(ex => {
        const key = ex.name;
        const prev = counts.get(key) || { count: 0, last: new Date(0) };
        const nextCount = prev.count + 1;
        const workoutDate = w.date instanceof Date ? w.date : new Date(w.date);
        const nextLast = workoutDate > prev.last ? workoutDate : prev.last;
        counts.set(key, { count: nextCount, last: nextLast });
      });
    });

    const top = Array.from(counts.entries())
      .map(([name, v]) => ({ name, count: v.count, lastUsed: v.last }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return b.lastUsed.getTime() - a.lastUsed.getTime();
      })
      .slice(0, 7);

    setTopExercises(top);

    // Derive exercise series from top exercises (one line per exercise name)
    const exerciseNames = top.map(t => t.name);
    const seriesSet = new Set<string>(exerciseNames);

    // Build per-date max weight per exercise for the filtered workouts
    const perDate = filtered.map(w => {
      const workoutDate = w.date instanceof Date ? w.date : new Date(w.date);
      const entry: Record<string, any> = { date: format(workoutDate, 'MMM dd') };
      
      // Initialize with undefined instead of null to let lines connect
      exerciseNames.forEach(n => { 
        entry[n] = undefined; 
      });
      
      w.exercises.forEach((ex: any) => {
        if (!seriesSet.has(ex.name)) return;
        
        let weight = 0;
        
        // Get weight using the same logic as other calculations
        if (ex.setsData && Array.isArray(ex.setsData) && ex.setsData.length > 0) {
          // New format: use max weight from setsData
          weight = Math.max(...ex.setsData.map((set: any) => set.weight || 0));
        } else if (typeof ex.weight === 'number') {
          // Fallback: use exercise weight
          weight = ex.weight;
        } else if (ex.sets && Array.isArray(ex.sets) && ex.sets.length > 0) {
          // Old format: use max weight from sets array
          weight = Math.max(...ex.sets.map((set: any) => set.weight || 0));
        }
        
        const current = entry[ex.name];
        entry[ex.name] = current == null ? weight : Math.max(current, weight);
      });
      
      return entry;
    });
    setLineData(perDate);

    // Build muscle distribution pie data using the efficient lookup map
    const muscleCounts = new Map<string, number>();
    for (const w of filtered) {
      for (const ex of w.exercises) {
        const def = newExerciseMap.get(ex.name); // ✅ O(1) lookup instead of API call
        const muscle = def?.muscleGroup;
        if (!muscle) continue;
        muscleCounts.set(muscle, (muscleCounts.get(muscle) || 0) + 1);
      }
    }
    const COLORS: Record<string, string> = {
      chest: '#E91E63',
      back: '#2196F3',
      shoulders: '#FF9800',
      biceps: '#9C27B0',
      triceps: '#00BCD4',
      arms: '#8BC34A',
      legs: '#4CAF50',
      abs: '#FFC107',
    };
    const pie = Array.from(muscleCounts.entries())
      .map(([name, value]) => ({ name, value, color: COLORS[name] || '#8884d8' }))
      .sort((a, b) => b.value - a.value);
    setPieData(pie);

    // Calculate workout summary statistics
    const totalWorkouts = filtered.length;
    let totalVolume = 0;
    const uniqueExerciseNames = new Set<string>();
    let totalDuration = 0;

    filtered.forEach(workout => {
      // Count unique exercises
      workout.exercises.forEach(ex => uniqueExerciseNames.add(ex.name));
      
      // Calculate total volume using the same logic as chart data
      const workoutVolume = workout.exercises.reduce((sum: number, ex: any) => {
        let exerciseVolume = 0;
        
        if (ex.setsData && Array.isArray(ex.setsData) && ex.setsData.length > 0) {
          // New format: use setsData array
          exerciseVolume = ex.setsData.reduce((setSum: number, set: any) => setSum + ((set.reps || 0) * (set.weight || 0)), 0);
        } else if (typeof ex.completedSets === 'number' && typeof ex.reps === 'number') {
          // Fallback: use completedSets * reps * weight
          exerciseVolume = ex.completedSets * ex.reps * (ex.weight || 0);
        } else if (ex.sets && Array.isArray(ex.sets) && ex.sets.length > 0) {
          // Old format: use sets array
          exerciseVolume = ex.sets.reduce((setSum: number, set: any) => setSum + ((set.reps || 0) * (set.weight || 0)), 0);
        }
        
        return sum + exerciseVolume;
      }, 0);
      
      totalVolume += workoutVolume;
      
      // Parse duration (assuming it's in "MM:SS" or "HH:MM:SS" format)
      if (workout.duration) {
        const parts = workout.duration.split(':').map(Number);
        if (parts.length === 2) {
          totalDuration += parts[0]; // minutes
        } else if (parts.length === 3) {
          totalDuration += parts[0] * 60 + parts[1]; // hours to minutes + minutes
        }
      }
    });

    const stats = {
      totalWorkouts,
      totalVolume,
      uniqueExercises: uniqueExerciseNames.size,
      avgDuration: totalWorkouts > 0 ? Math.round(totalDuration / totalWorkouts) : 0
    };

    console.log('📊 [PROGRESS] Calculated workout stats:', stats);
      } catch (e: any) {
        console.error('Failed to load workout history for progress page', e);
        setError(t('progress.loadError'));
      } finally {
        setLoading(false);
      }
    };
    loadHistory();
  }, [t, user, timeRange]);

  return (
    <>
      <StructuredData type="WebApplication" name="Progress Tracking - Free Weightlifting Planner" />
      <Helmet>
        <title>{seoConfig.progress.title}</title>
        <meta name="description" content={seoConfig.progress.description} />
        {seoConfig.progress.keywords && <meta name="keywords" content={seoConfig.progress.keywords} />}
        <meta property="og:title" content={seoConfig.progress.title} />
        <meta property="og:description" content={seoConfig.progress.description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={seoConfig.progress.title} />
        <meta name="twitter:description" content={seoConfig.progress.description} />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      <PageShell variant="content">
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p className={styles.loadingText}>{t('progress.loading')}</p>
          </div>
        ) : (
          <>
            <h1 className="content-title">{t('progress.title')}</h1>
            {error && (
              <div className="error-message" style={{ marginTop: '10px', marginBottom: '10px', color: '#f87171' }}>
                {error}
              </div>
            )}
            <div className={styles.chartControls} style={{ marginBottom: '20px', marginTop: '10px' }}>
              <div className={styles.timeRangeSelector}>
                <span>{t('progress.timeRange.label')} </span>
                <select
                  value={timeRange || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTimeRange(value ? parseInt(value) : null);
                  }}
                >
                  <option value="">{t('progress.timeRange.allTime')}</option>
                  <option value="7">{t('progress.timeRange.lastDays', { count: 7 })}</option>
                  <option value="30">{t('progress.timeRange.lastDays', { count: 30 })}</option>
                  <option value="90">{t('progress.timeRange.lastDays', { count: 90 })}</option>
                  <option value="180">{t('progress.timeRange.lastMonths', { count: 6 })}</option>
                  <option value="365">{t('progress.timeRange.lastYear')}</option>
                </select>
              </div>
            </div>

            <div className={styles.muscleDistribution} style={{ marginTop: '30px' }}>
              <h3>
                {t('progress.muscleFocus')}
                {timeRange ? ` ${t('progress.window', { count: timeRange })}` : ''}
              </h3>
              {pieData.length > 0 ? (
              <div style={{ height: 300, width: '100%' }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Tooltip contentStyle={{ backgroundColor: '#2a2a2a', border: '1px solid #444', borderRadius: 4, color: '#fff' }} />
                    <Legend />
                    <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={110} label>
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="no-data-message">{t('progress.noMuscleData')}</div>
            )}
            </div>

            <h3>
              {t('progress.maxWeight')}
              {timeRange ? ` ${t('progress.window', { count: timeRange })}` : ''}
            </h3>
            <div style={{ height: '400px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={lineData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                    bottom: 5,
                  }}
                >
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#fff' }} 
                  tickLine={{ stroke: '#666' }}
                />
                <YAxis 
                  width={20}
                  tick={{ fill: '#fff', fontSize: 10 }} 
                  tickLine={{ stroke: '#666' }}
                  tickMargin={2}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#2a2a2a', 
                    border: '1px solid #444',
                    borderRadius: '4px',
                    color: '#fff'
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                  {lineData.length > 0 && Object.keys(lineData[0]).filter(key => key !== 'date').map((key, index) => (
                  <Line 
                    key={key}
                    type="monotone" 
                    dataKey={key} 
                    connectNulls={true}
                    stroke={['#4CAF50','#2196F3','#FF9800','#9C27B0','#E91E63','#00BCD4','#8BC34A'][index % 7]}
                    strokeWidth={2}
                    dot={{ fill: ['#4CAF50','#2196F3','#FF9800','#9C27B0','#E91E63','#00BCD4','#8BC34A'][index % 7], r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className={styles.topExercises} style={{ marginTop: '20px' }}>
              <h3>
                {t('progress.topExercises')}
                {timeRange ? ` ${t('progress.window', { count: timeRange })}` : ''}
              </h3>
              {topExercises.length > 0 ? (
              <ul>
                {topExercises.map(ex => (
                  <li 
                    key={ex.name}
                    onClick={() => {
                      const exerciseDef = exerciseMap.get(ex.name);
                      if (exerciseDef) setSelectedExercise(exerciseDef);
                    }}
                    style={{ cursor: 'pointer' }}
                    className={styles.clickableExercise}
                  >
                    <span style={{ fontWeight: 600 }}>{ex.name}</span>
                    <span style={{ opacity: 0.8 }}> — {t('progress.workoutCount', { count: ex.count })}</span>
                    <span style={{ opacity: 0.6 }}> • {t('progress.lastUsed', { date: format(ex.lastUsed, 'MMM dd') })}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="no-data-message">{t('progress.noExerciseData')}</div>
            )}
            </div>
            
            {selectedExercise && (
              <ExerciseInfoModal
                exercise={selectedExercise}
                onClose={() => setSelectedExercise(null)}
                userId={user?.id}
              />
            )}
          </>
        )}
      </PageShell>
    </>
  );
};

export default ProgressPage;
